/**
 * Mobile Continuous Slider Enhancement
 * Adds scroll-based navigation for mobile continuous slider
 * Works alongside the existing ProductGallery class
 */
(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 799;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  class MobileSliderEnhancer {
    constructor(container) {
      this.container = container;
      this.imageContainer = container.querySelector('.shop-image-container');
      this.mainImages = Array.from(container.querySelectorAll('.shop-main-image'));
      this.thumbnails = Array.from(container.querySelectorAll('.shop-thumbnail'));
      this.dots = Array.from(container.querySelectorAll('.shop-dot'));
      this.indicatorsContainer = container.querySelector('.shop-indicators, #thumbnail-indicators');

      this.currentIndex = 0;
      this.isScrolling = false;
      this.scrollTimeout = null;

      if (this.imageContainer && this.mainImages.length > 1) {
        this.init();
      }
    }

    init() {
      if (!isMobile()) return;

      // Listen for scroll on the image container
      this.imageContainer.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

      // Update on resize
      window.addEventListener('resize', this.handleResize.bind(this));

      // Handle thumbnail clicks to scroll
      this.thumbnails.forEach((thumb, i) => {
        thumb.addEventListener('click', (e) => {
          if (isMobile()) {
            e.stopPropagation();
            this.scrollToImage(i);
          }
        });
      });

      // Handle dot clicks to scroll
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
          if (isMobile()) {
            e.stopPropagation();
            this.scrollToImage(i);
          }
        });
      });
    }

    handleScroll() {
      if (!isMobile()) return;

      // Debounce scroll end detection
      clearTimeout(this.scrollTimeout);
      this.isScrolling = true;

      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
        this.updateActiveFromScroll();
      }, 100);

      // Update indicators during scroll for responsive feel
      this.updateActiveFromScroll();
    }

    updateActiveFromScroll() {
      if (!this.imageContainer) return;

      const containerRect = this.imageContainer.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      this.mainImages.forEach((img, i) => {
        const imgRect = img.getBoundingClientRect();
        const imgCenter = imgRect.left + imgRect.width / 2;
        const distance = Math.abs(containerCenter - imgCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
        this.updateIndicators(closestIndex);
      }
    }

    updateIndicators(index) {
      // Update thumbnails
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
      });

      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });

      // Update custom indicators if present
      if (this.indicatorsContainer) {
        Array.from(this.indicatorsContainer.children).forEach((dot, i) => {
          dot.classList.toggle('active', i === index);
        });
      }

      // Scroll thumbnail into view
      this.scrollThumbnailIntoView(index);
    }

    scrollToImage(index) {
      if (!this.imageContainer || !this.mainImages[index]) return;

      const image = this.mainImages[index];
      const containerWidth = this.imageContainer.offsetWidth;
      const imageWidth = image.offsetWidth;

      // Calculate scroll position to center the image
      const scrollLeft = image.offsetLeft - (containerWidth - imageWidth) / 2;

      this.imageContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });

      this.currentIndex = index;
      this.updateIndicators(index);
    }

    scrollThumbnailIntoView(index) {
      const thumbnail = this.thumbnails[index];
      if (!thumbnail) return;

      const container = thumbnail.parentElement;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;
      const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }

    handleResize() {
      // Reset scroll position on orientation change
      if (isMobile() && this.imageContainer) {
        this.scrollToImage(this.currentIndex);
      }
    }
  }

  // Initialize on all galleries
  function initMobileSliders() {
    document.querySelectorAll('.product-gallery').forEach((container) => {
      if (!container.__mobileSliderEnhancer) {
        container.__mobileSliderEnhancer = new MobileSliderEnhancer(container);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileSliders);
  } else {
    initMobileSliders();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', initMobileSliders);
})();
